/**
 * 🎓 Phase 15.1 — Tutorial Context Provider
 *
 * - Loads the user's tutorial progress on mount (if authed)
 * - Exposes useTutorial() hook
 * - Auto-launches the first-time walkthrough on first login after
 *   signup (when user has never had first_launched_at set AND skipped
 *   is false AND completed_at is null)
 * - Persists every state transition to the backend so progress
 *   survives across devices / sessions
 */
import {
  createContext, useContext, useEffect, useState, useCallback, useMemo,
} from "react";
import api from "@/lib/api";
import { TUTORIAL_SECTIONS, stepsFor, hasShippedContent } from "@/components/tutorial/sectionContent";

const TutorialContext = createContext(null);

/**
 * 🛡️ Phase 15.2 hardening — hardcoded catalog fallback.
 *
 * If `/api/tutorial/catalog` is unreachable (stale backend deploy,
 * network blip, CORS), the Help Center MUST still render the full
 * 10-section grid. This constant mirrors the authoritative backend
 * catalog in tutorial_routes.py — keep in sync there.
 */
const FALLBACK_CATALOG = [
  { id: "upload_track",      title: "Upload Your First Track",        emoji: "🎤", critical: true,  order: 1,  shipped: true,
    blurb: "Get your music onto RGM Studio in under 60 seconds." },
  { id: "edit_profile",      title: "Edit Your Profile",              emoji: "🪪", critical: true,  order: 2,  shipped: true,
    blurb: "Avatar, bio, socials — make your profile look like you." },
  { id: "feature_spotlight", title: "Feature + Spotlight System",     emoji: "✨", critical: true,  order: 3,  shipped: true,
    blurb: "How tracks get featured, the surge drip, and Spotlight winners." },
  { id: "live_beam",         title: "Join or Create a Live Beam",     emoji: "🎙️", critical: true,  order: 4,  shipped: true,
    blurb: "Go live, invite supporters, get gifted in real time." },
  { id: "coins_gifts",       title: "Coins & Gifts",                  emoji: "💎", critical: false, order: 5,  shipped: false,
    blurb: "Buy coins, send gifts, top your favorite creators." },
  { id: "crews_community",   title: "Crews & Community",              emoji: "🤝", critical: false, order: 6,  shipped: false,
    blurb: "Join a crew, build your circle, post in the Community feed." },
  { id: "referrals",         title: "Referrals",                      emoji: "📨", critical: false, order: 7,  shipped: false,
    blurb: "Invite friends → earn rewards → climb the referral leaderboard." },
  { id: "rankings",          title: "Rankings & Charts",              emoji: "📈", critical: false, order: 8,  shipped: false,
    blurb: "How charts work — momentum chips, weekly recaps, hot tracks." },
  { id: "share_repost",      title: "Share & Repost Music",           emoji: "🔁", critical: false, order: 9,  shipped: false,
    blurb: "Share to socials with branded cards. Repost tracks to your followers." },
  { id: "notifications",     title: "Notifications",                  emoji: "🔔", critical: false, order: 10, shipped: false,
    blurb: "Stay on top of plays, gifts, lives, and crew activity." },
];
const FALLBACK_CRITICAL_IDS = FALLBACK_CATALOG.filter((s) => s.critical).map((s) => s.id);

const initialState = {
  loaded: false,
  progress: null,
  activeSection: null,  // section id currently being walked through
  activeStep: 0,
  source: null,         // 'auto' | 'help_center' | 'replay'
  catalogSource: "unknown",  // 'api' | 'fallback' — for debugging banners
  catalogError: null,        // last error message (visible in Help Center)
};

export const TutorialProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  const isAuthed = () => !!localStorage.getItem("session_token");

  const loadProgress = useCallback(async () => {
    if (!isAuthed()) {
      // Even when not authed, fetch the public catalog so the Help
      // Center can render the 10-section grid (with all sections in
      // "not_started" state) for marketing / preview purposes.
      try {
        const r = await api.get("/tutorial/catalog");
        const sections = r.data?.sections;
        const useFallback = !Array.isArray(sections) || sections.length === 0;
        setState((s) => ({
          ...s, loaded: true,
          catalogSource: useFallback ? "fallback" : "api",
          catalogError: null,
          progress: {
            catalog: useFallback ? FALLBACK_CATALOG : sections,
            critical_section_ids: useFallback
              ? FALLBACK_CRITICAL_IDS
              : (r.data?.critical_section_ids || FALLBACK_CRITICAL_IDS),
            completed_sections: [], completed_steps: {},
            skipped: false, first_launched_at: null, completed_at: null,
          },
        }));
      } catch (err) {
        setState((s) => ({
          ...s, loaded: true,
          catalogSource: "fallback",
          catalogError: err?.message || "catalog_fetch_failed",
          progress: {
            catalog: FALLBACK_CATALOG,
            critical_section_ids: FALLBACK_CRITICAL_IDS,
            completed_sections: [], completed_steps: {},
            skipped: false, first_launched_at: null, completed_at: null,
          },
        }));
      }
      return null;
    }
    try {
      const r = await api.get("/tutorial/me");
      const progress = r.data?.progress || null;
      // Ensure catalog is always populated even if backend omits it.
      if (progress) {
        const hasCatalog = Array.isArray(progress.catalog) && progress.catalog.length > 0;
        if (!hasCatalog) {
          progress.catalog = FALLBACK_CATALOG;
          progress.critical_section_ids = progress.critical_section_ids?.length
            ? progress.critical_section_ids : FALLBACK_CRITICAL_IDS;
        }
      }
      setState((s) => ({
        ...s, loaded: true,
        catalogSource: progress?.catalog === FALLBACK_CATALOG ? "fallback" : "api",
        catalogError: null,
        progress: progress || {
          catalog: FALLBACK_CATALOG,
          critical_section_ids: FALLBACK_CRITICAL_IDS,
          completed_sections: [], completed_steps: {},
          skipped: false, first_launched_at: null, completed_at: null,
        },
      }));
      return progress;
    } catch (err) {
      // Hard failure: render Help Center with hardcoded fallback so the
      // modal is never blank on production.
      setState((s) => ({
        ...s, loaded: true,
        catalogSource: "fallback",
        catalogError: err?.message || "progress_fetch_failed",
        progress: {
          catalog: FALLBACK_CATALOG,
          critical_section_ids: FALLBACK_CRITICAL_IDS,
          completed_sections: [], completed_steps: {},
          skipped: false, first_launched_at: null, completed_at: null,
        },
      }));
      return null;
    }
  }, []);

  // Initial mount load + react to auth changes via the storage event.
  useEffect(() => {
    loadProgress();
    const onStorage = (e) => {
      if (e.key === "session_token") loadProgress();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [loadProgress]);

  // Auto-launch the first-time walkthrough on first login after signup.
  // Only fires when authed (the unauth catalog fetch sets an empty
  // progress shell, which would otherwise trip the auto-launch heuristic).
  useEffect(() => {
    if (!state.loaded || !state.progress) return;
    if (!isAuthed()) return;
    const p = state.progress;
    const shouldAutoLaunch =
      !p.first_launched_at
      && !p.skipped
      && !p.completed_at
      && state.activeSection === null;
    if (shouldAutoLaunch) {
      (async () => {
        try {
          await api.post("/tutorial/launch");
        } catch { /* fire-and-forget */ }
        // Open the first critical section automatically.
        startSection("upload_track", { source: "auto" });
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loaded, state.progress?.first_launched_at]);

  const startSection = useCallback((sectionId, { source = "help_center" } = {}) => {
    if (!TUTORIAL_SECTIONS[sectionId] && !hasShippedContent(sectionId)) {
      // Unshipped section — bail silently; Help Center shows the coming-soon tile.
      return;
    }
    setState((s) => ({ ...s, activeSection: sectionId, activeStep: 0, source }));
    if (isAuthed()) {
      api.post(`/tutorial/start/${sectionId}`).catch(() => {});
    }
  }, []);

  const nextStep = useCallback(() => {
    setState((s) => {
      const total = stepsFor(s.activeSection).length;
      const next = s.activeStep + 1;
      if (next >= total) {
        // Section complete.
        if (isAuthed() && s.activeSection) {
          api.post(`/tutorial/complete/${s.activeSection}`)
            .then((r) => setState((cur) => ({
              ...cur, progress: r.data?.progress || cur.progress,
            })))
            .catch(() => {});
        }
        return { ...s, activeSection: null, activeStep: 0, source: null };
      }
      if (isAuthed() && s.activeSection) {
        api.post(`/tutorial/step/${s.activeSection}`, { step: next }).catch(() => {});
      }
      return { ...s, activeStep: next };
    });
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => ({ ...s, activeStep: Math.max(0, s.activeStep - 1) }));
  }, []);

  const exitSection = useCallback(({ kind = "exited" } = {}) => {
    setState((s) => {
      if (isAuthed() && s.activeSection) {
        api.post("/tutorial/event", {
          section: s.activeSection, step: s.activeStep, action: kind,
        }).catch(() => {});
      }
      return { ...s, activeSection: null, activeStep: 0, source: null };
    });
  }, []);

  const skipAll = useCallback(async () => {
    setState((s) => ({ ...s, activeSection: null, activeStep: 0, source: null }));
    if (isAuthed()) {
      try {
        const r = await api.post("/tutorial/skip");
        setState((s) => ({ ...s, progress: r.data?.progress || s.progress }));
      } catch { /* swallow */ }
    }
  }, []);

  const replaySection = useCallback(async (sectionId) => {
    if (!isAuthed()) return;
    try {
      const r = await api.post(`/tutorial/replay/${sectionId}`);
      setState((s) => ({
        ...s,
        progress: r.data?.progress || s.progress,
        activeSection: sectionId, activeStep: 0, source: "replay",
      }));
    } catch { /* swallow */ }
  }, []);

  const value = useMemo(() => ({
    ...state,
    loadProgress,
    startSection,
    nextStep,
    prevStep,
    exitSection,
    skipAll,
    replaySection,
    catalog: (state.progress?.catalog && state.progress.catalog.length > 0)
      ? state.progress.catalog : FALLBACK_CATALOG,
    criticalIds: (state.progress?.critical_section_ids && state.progress.critical_section_ids.length > 0)
      ? state.progress.critical_section_ids : FALLBACK_CRITICAL_IDS,
  }), [state, loadProgress, startSection, nextStep, prevStep, exitSection, skipAll, replaySection]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    // Render-safe fallback (e.g. components imported outside provider).
    return { loaded: false, progress: null, activeSection: null, activeStep: 0,
             startSection: () => {}, nextStep: () => {}, prevStep: () => {},
             exitSection: () => {}, skipAll: () => {}, replaySection: () => {},
             catalog: FALLBACK_CATALOG, criticalIds: FALLBACK_CRITICAL_IDS };
  }
  return ctx;
};
